import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { RefreshCw, Plus, Edit3, Trash2, AlertTriangle, Filter, Search, Paperclip, Download, X, Eye, EyeOff, MessagesSquare, ListChecks, Clock, CheckCircle2, Ban, Flame } from "lucide-react";
import { toast } from "sonner";
import LightRichEditor from "./LightRichEditor";
import CRCommentsThread from "@/components/shared/CRCommentsThread";
import SummaryCards from "./SummaryCards";
import { fmtDate } from "./crmUtils";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
};
import {
  crmListChangeRequests, crmCreateChangeRequest, crmUpdateChangeRequest, crmDeleteChangeRequest,
  crmUploadCRDocument, crmDeleteCRDocument, crmCRDocumentUrl,
  crmList, adminListClients, apiErrorMsg,
} from "@/lib/api";

const STATUS_OPTIONS = ["Open", "In Progress", "Done", "Blocked", "Rejected"];
const SEVERITY_OPTIONS = ["Critical", "Major", "Minor"];

const STATUS_TONE = {
  "Open":        "bg-sky-100 text-sky-800 border-sky-300",
  "In Progress": "bg-amber-100 text-amber-800 border-amber-300",
  "Done":        "bg-emerald-100 text-emerald-800 border-emerald-400",
  "Blocked":     "bg-rose-100 text-rose-800 border-rose-300",
  "Rejected":    "bg-zinc-200 text-zinc-700 border-zinc-400",
};
const SEVERITY_TONE = {
  "Critical": "bg-rose-100 text-rose-800 border-rose-400",
  "Major":    "bg-orange-100 text-orange-800 border-orange-400",
  "Minor":    "bg-blue-100 text-blue-800 border-blue-300",
};

const EMPTY = {
  project_id: "", title: "", description: "", expectation: "",
  raised_date: new Date().toISOString().slice(0, 10),
  closed_date: "",
  status: "Open", severity: "Minor", client_visible: true,
};

export default function ChangeRequests({ token, currentUser = null }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { editing?, form }
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "all", severity: "all", project_id: "all", client_id: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const data = await crmListChangeRequests(token, params);
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load change requests"));
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    adminListClients(token).then(setClients).catch(() => {});
    crmList(token, "projects").then(setProjects).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    return (`${r.title} ${r.project_name} ${r.client_name}`).toLowerCase().includes(q.toLowerCase());
  }), [rows, q]);

  // KPI counts — computed from the server-filtered `rows` so cards reflect
  // whatever Project/Client/Worktype filters the user is currently applying,
  // independent of the text-search box.
  const kpiCounts = useMemo(() => {
    const c = { total: rows.length, open: 0, inprogress: 0, blocked: 0, done: 0, critical: 0 };
    for (const r of rows) {
      if (r.status === "Open") c.open += 1;
      else if (r.status === "In Progress") c.inprogress += 1;
      else if (r.status === "Blocked") c.blocked += 1;
      else if (r.status === "Done") c.done += 1;
      if (r.severity === "Critical" && r.status !== "Done" && r.status !== "Rejected") c.critical += 1;
    }
    return c;
  }, [rows]);

  // Clicking a card toggles that status filter (Total resets)
  const setStatusFilter = (statusKey) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === statusKey ? "all" : statusKey,
    }));
  };
  const setSeverityFilter = (sev) => {
    setFilters((prev) => ({
      ...prev,
      severity: prev.severity === sev ? "all" : sev,
    }));
  };

  const openNew = () => setModal({ editing: null, form: { ...EMPTY } });
  const openEdit = (row) => setModal({
    editing: row,
    form: {
      project_id: row.project_id || "",
      title: row.title || "",
      description: row.description || "",
      expectation: row.expectation || "",
      raised_date: (row.raised_date || "").slice(0, 10),
      closed_date: (row.closed_date || "").slice(0, 10),
      status: row.status || "Open",
      severity: row.severity || "Minor",
      client_visible: row.client_visible !== false,
    },
  });

  const save = async () => {
    if (!modal) return;
    const { editing, form } = modal;
    if (!form.project_id) return toast.error("Select a project");
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editing) {
        await crmUpdateChangeRequest(token, editing.id, form);
        toast.success("Change request updated");
      } else {
        await crmCreateChangeRequest(token, form);
        toast.success("Change request created");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete change request "${row.title}"?`)) return;
    try {
      await crmDeleteChangeRequest(token, row.id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const clientMap = useMemo(() => Object.fromEntries(
    clients.map((c) => [c.id, c.name || c.dashboard_name || c.username])
  ), [clients]);

  return (
    <div data-testid="crm-change-requests-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Change Requests</div>
          <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">Project change requests</h2>
          <p className="mt-2 text-sm text-[#4a4a4a]">
            Log CRs raised against any project. Toggle <b>Client visible</b> to show it inside the client&rsquo;s portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="crm-cr-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722] transition-colors">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={openNew} data-testid="crm-cr-new"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] transition-colors">
            <Plus className="h-4 w-4" /> New CR
          </button>
        </div>
      </div>

      {/* KPI cards — filter-aware, click a card to toggle that status filter */}
      <SummaryCards cols="grid-cols-2 md:grid-cols-3 lg:grid-cols-6" cards={[
        {
          label: "Total CRs", value: kpiCounts.total,
          icon: <ListChecks className="h-4 w-4 text-[#0a0a0a]" />,
          tone: "bg-[#0a0a0a]",
        },
        {
          label: "Open", value: kpiCounts.open,
          icon: <AlertTriangle className="h-4 w-4 text-sky-600" />,
          tone: "bg-sky-500", accent: "text-sky-700",
          onClick: () => setStatusFilter("Open"),
          active: filters.status === "Open",
          clickLabel: "click to filter", activeLabel: "✓ filtered",
        },
        {
          label: "In Progress", value: kpiCounts.inprogress,
          icon: <Clock className="h-4 w-4 text-amber-600" />,
          tone: "bg-amber-500", accent: "text-amber-700",
          onClick: () => setStatusFilter("In Progress"),
          active: filters.status === "In Progress",
          clickLabel: "click to filter", activeLabel: "✓ filtered",
        },
        {
          label: "Blocked", value: kpiCounts.blocked,
          icon: <Ban className="h-4 w-4 text-rose-600" />,
          tone: "bg-rose-500", accent: "text-rose-700",
          onClick: () => setStatusFilter("Blocked"),
          active: filters.status === "Blocked",
          clickLabel: "click to filter", activeLabel: "✓ filtered",
        },
        {
          label: "Done", value: kpiCounts.done,
          icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
          tone: "bg-emerald-500", accent: "text-emerald-700",
          onClick: () => setStatusFilter("Done"),
          active: filters.status === "Done",
          clickLabel: "click to filter", activeLabel: "✓ filtered",
        },
        {
          label: "Critical · Active", value: kpiCounts.critical,
          icon: <Flame className="h-4 w-4 text-rose-600" />,
          tone: "bg-rose-600", accent: "text-rose-700",
          onClick: () => setSeverityFilter("Critical"),
          active: filters.severity === "Critical",
          sub: "Non-closed criticals",
          clickLabel: "click to filter", activeLabel: "✓ filtered",
        },
      ]} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, project, client…"
            data-testid="crm-cr-search"
            className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-64 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
        </div>
        <FilterSel label="Status" value={filters.status} testid="crm-cr-filter-status"
          onChange={(v) => setFilters({ ...filters, status: v })}
          options={[{ k: "all", label: "All" }, ...STATUS_OPTIONS.map((s) => ({ k: s, label: s }))]} />
        <FilterSel label="Severity" value={filters.severity} testid="crm-cr-filter-severity"
          onChange={(v) => setFilters({ ...filters, severity: v })}
          options={[{ k: "all", label: "All" }, ...SEVERITY_OPTIONS.map((s) => ({ k: s, label: s }))]} />
        <FilterSel label="Project" value={filters.project_id} testid="crm-cr-filter-project"
          onChange={(v) => setFilters({ ...filters, project_id: v })}
          options={[{ k: "all", label: "All" }, ...projects.map((p) => ({ k: p.id, label: p.name }))]} />
        <FilterSel label="Client" value={filters.client_id} testid="crm-cr-filter-client"
          onChange={(v) => setFilters({ ...filters, client_id: v })}
          options={[{ k: "all", label: "All" }, ...clients.map((c) => ({ k: c.id, label: c.name || c.username }))]} />
      </div>

      {/* Table */}
      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-sm text-[#4a4a4a]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-sm text-[#4a4a4a]">
            No change requests yet. Click <b>+ New CR</b> to add one.
          </div>
        ) : (
          <table className="w-full text-left min-w-[1200px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3 w-12">S.no</th>
                <th className="px-4 py-3">Title / Project</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Raised by</th>
                <th className="px-4 py-3">Raised</th>
                <th className="px-4 py-3">Closed</th>
                <th className="px-4 py-3">Docs</th>
                <th className="px-4 py-3">Comments</th>
                <th className="px-4 py-3">Client visible</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} data-testid={`crm-cr-row-${r.id}`} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-[#0a0a0a]">{r.title}</div>
                    <div className="text-[11px] text-[#4a4a4a]">{r.project_name} · {r.client_name || "—"}</div>
                  </td>
                  <td className="px-4 py-4"><Pill cls={SEVERITY_TONE[r.severity] || "bg-zinc-100 border-zinc-300"} label={r.severity} /></td>
                  <td className="px-4 py-4"><Pill cls={STATUS_TONE[r.status] || "bg-zinc-100 border-zinc-300"} label={r.status} /></td>
                  <td className="px-4 py-4 text-xs">
                    <span className={`font-semibold ${r.raised_by === "client" ? "text-[#ff5722]" : "text-[#0a0a0a]"}`}>
                      {r.raised_by_name || (r.raised_by === "client" ? (r.client_name || "Client") : "Reachvel")}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDateTime(r.raised_at || r.created_at || r.raised_date) || "—"}</td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.closed_date) || "—"}</td>
                  <td className="px-4 py-4 text-xs">
                    {r.documents_count > 0
                      ? <span className="inline-flex items-center gap-1 text-[#0a0a0a]"><Paperclip className="h-3 w-3" /> {r.documents_count}</span>
                      : <span className="text-[#9a9a9a]">—</span>}
                  </td>
                  <td className="px-4 py-4 text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <MessagesSquare className={`h-3.5 w-3.5 ${r.comments_count > 0 ? "text-[#0a0a0a]" : "text-[#9a9a9a]"}`} />
                      {r.comments_count > 0 ? <span className="text-[#0a0a0a]">{r.comments_count}</span> : <span className="text-[#9a9a9a]">—</span>}
                      {r.unread_count > 0 && (
                        <span
                          data-testid={`crm-cr-unread-${r.id}`}
                          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff5722] text-white text-[10px] font-bold"
                          title={`${r.unread_count} new client comment${r.unread_count > 1 ? "s" : ""}`}
                        >
                          {r.unread_count}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs">
                    {r.client_visible
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Eye className="h-3 w-3" /> Yes</span>
                      : <span className="inline-flex items-center gap-1 text-[#9a9a9a]"><EyeOff className="h-3 w-3" /> No</span>}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => openEdit(r)} data-testid={`crm-cr-edit-${r.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722] mr-1">
                      <Edit3 className="h-3 w-3" /> Edit
                    </button>
                    <button onClick={() => remove(r)} data-testid={`crm-cr-delete-${r.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-rose-300 text-rose-700 rounded-full hover:bg-rose-50">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <CRModal
          modal={modal}
          projects={projects}
          currentUser={currentUser}
          onChange={(form) => setModal({ ...modal, form })}
          onSave={save}
          onClose={() => { setModal(null); load(); }}
          token={token}
        />
      )}
      {/* clientMap is used inside filters and detail views; suppressing unused-var warning by referencing it */}
      {false && clientMap}
    </div>
  );
}

function FilterSel({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2 border border-black/15 rounded-full px-3 py-1.5">
      <Filter className="h-3.5 w-3.5 text-[#9a9a9a]" />
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">{label}:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="font-mono text-xs bg-transparent outline-none cursor-pointer min-w-[100px]">
        {options.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Pill({ cls, label }) {
  return (
    <span className={`inline-block whitespace-nowrap px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${cls}`}>
      {label || "—"}
    </span>
  );
}

function CRModal({ modal, projects, currentUser, onChange, onSave, onClose, token }) {
  const { editing, form } = modal;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState(editing?.documents || []);

  const upload = async (file) => {
    if (!editing) {
      toast.error("Save the CR first, then upload documents");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    try {
      const updated = await crmUploadCRDocument(token, editing.id, file);
      setDocuments(updated.documents || []);
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    if (!editing) return;
    if (!window.confirm("Remove this document?")) return;
    try {
      await crmDeleteCRDocument(token, editing.id, docId);
      setDocuments(documents.filter((d) => d.id !== docId));
      toast.success("Removed");
    } catch {
      toast.error("Remove failed");
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const url = crmCRDocumentUrl(editing.id, doc.id);
      const res = await fetch(url, { headers: { "X-Admin-Token": token } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-lg border border-black/10 shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-black/10 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">
              {editing ? "Edit change request" : "New change request"}
            </div>
            <h3 className="text-lg font-bold text-[#0a0a0a]">{form.title || "Untitled CR"}</h3>
          </div>
          <button onClick={onClose} data-testid="crm-cr-modal-close" className="p-1.5 rounded hover:bg-black/5">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Project *">
              <select value={form.project_id} onChange={(e) => onChange({ ...form, project_id: e.target.value })}
                data-testid="crm-cr-input-project" className="input">
                <option value="">— Select project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Title *">
              <input value={form.title} onChange={(e) => onChange({ ...form, title: e.target.value })}
                data-testid="crm-cr-input-title" className="input" placeholder="e.g. Add address auto-fill on checkout" />
            </Field>
            <Field label="Severity">
              <select value={form.severity} onChange={(e) => onChange({ ...form, severity: e.target.value })}
                data-testid="crm-cr-input-severity" className="input">
                {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => onChange({ ...form, status: e.target.value })}
                data-testid="crm-cr-input-status" className="input">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Raised date">
              <input type="date" value={form.raised_date} onChange={(e) => onChange({ ...form, raised_date: e.target.value })}
                data-testid="crm-cr-input-raised" className="input" />
            </Field>
            <Field label="Closed date">
              <input type="date" value={form.closed_date} onChange={(e) => onChange({ ...form, closed_date: e.target.value })}
                data-testid="crm-cr-input-closed" className="input" />
            </Field>
          </div>

          <Field label="Description *">
            <LightRichEditor
              value={form.description}
              onChange={(v) => onChange({ ...form, description: v })}
              placeholder="Describe the change. Paste screenshots directly."
              testid="crm-cr-input-description"
            />
          </Field>

          <Field label="Expectation (optional)">
            <textarea value={form.expectation} onChange={(e) => onChange({ ...form, expectation: e.target.value })}
              data-testid="crm-cr-input-expectation" rows={2} className="input"
              placeholder="What outcome does the client expect? e.g. 'Should reduce checkout drop-off by 20%'" />
          </Field>

          <div className="flex items-center gap-3 pt-2 border-t border-black/10">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.client_visible} onChange={(e) => onChange({ ...form, client_visible: e.target.checked })}
                data-testid="crm-cr-input-client-visible" className="h-4 w-4 accent-[#ff5722]" />
              <span className="text-sm">Show this CR in the client&rsquo;s portal</span>
            </label>
          </div>

          {editing && (
            <div className="pt-3 border-t border-black/10">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a]">Documents ({documents.length})</div>
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="crm-cr-upload-btn"
                  className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
                  <Paperclip className="h-3 w-3" /> {uploading ? "Uploading…" : "Attach file"}
                </button>
                <input ref={fileRef} type="file" hidden onChange={(e) => {
                  const f = e.target.files?.[0]; e.target.value = "";
                  if (f) upload(f);
                }} data-testid="crm-cr-file-input" />
              </div>
              {documents.length === 0
                ? <div className="text-xs text-[#4a4a4a] italic">No documents attached.</div>
                : <ul className="space-y-1">
                    {documents.map((d) => (
                      <li key={d.id} className="flex items-center justify-between border border-black/10 rounded px-3 py-1.5 text-xs">
                        <span className="truncate">{d.filename} <span className="text-[#9a9a9a]">({Math.round(d.size / 1024)} KB)</span></span>
                        <span className="flex items-center gap-1">
                          <button onClick={() => downloadDoc(d)} data-testid={`crm-cr-doc-download-${d.id}`}
                            className="p-1 hover:text-[#ff5722]"><Download className="h-3.5 w-3.5" /></button>
                          <button onClick={() => removeDoc(d.id)} data-testid={`crm-cr-doc-remove-${d.id}`}
                            className="p-1 text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                        </span>
                      </li>
                    ))}
                  </ul>}
            </div>
          )}

          {/* Two-way comments thread — visible only after CR is saved */}
          {editing ? (
            <CRCommentsThread
              crId={editing.id}
              token={token}
              mode="admin"
              currentRole={currentUser?.role || "superadmin"}
              currentUserId={currentUser?.id || ""}
            />
          ) : (
            <div className="pt-3 border-t border-black/10 text-xs text-[#9a9a9a] italic">
              Save the CR first to start a comments thread with the client.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-black/10 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-black/15 rounded-full hover:bg-black/5">Cancel</button>
          <button onClick={onSave} data-testid="crm-cr-modal-save"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a]">
            {editing ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// Minimal input styles (piggyback on existing global .input class if present)
// This adds a fallback in case no global class exists.
const _style = document.createElement("style");
_style.textContent = `
  .input {
    display: block; width: 100%;
    border: 1px solid rgba(0,0,0,0.15); border-radius: 6px;
    padding: 6px 10px; font-size: 13px; outline: none;
    background: #fff;
  }
  .input:focus { border-color: #ff5722; box-shadow: 0 0 0 2px rgba(255,87,34,0.2); }
`;
if (typeof document !== "undefined" && !document.querySelector("[data-crm-input-style]")) {
  _style.setAttribute("data-crm-input-style", "");
  document.head.appendChild(_style);
}

// Suppress unused imports lint warnings
void AlertTriangle;
